//@flow
import React, {useEffect, useState, useCallback} from 'react';
import {
  makeStyles,
  Typography,
  FormControlLabel,
  CircularProgress,
  Backdrop,
  Fade,
} from '@material-ui/core';
import {useParams, useHistory} from 'react-router-dom';

import InformationSection from './sections/Information';
import ContentSection from './sections/Content';
import SeoSection from './sections/Seo';
import IosSwitch from '../../../../elements/IosSwitch/IosSwitch';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {FormProvider, useForm} from 'react-hook-form';
import {BLOG_FORM_FIELDS} from '../../constants';
import RelatedProducts from './sections/RelatedProducts';
import {
  useBlogPost,
  useBlogPostActions,
  useBlogFormDraftValue,
} from '../../utils/blog-post';
import Toast from '../../../../elements/Toast';
import {useToastActions} from '../../../toast-alert/utils';

import type {BlogFormFields} from '../../constants';
import PostFeatured from './sections/PostFeatured';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    height: '100%',
    paddingBottom: '2rem',
    position: 'relative',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: '#ffffff95',
    zIndex: 10,
    cursor: 'progress',
    backdropFilter: 'blur(2px)',
  },
  loadingBackdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
    backgroundColor: 'rgba(228, 228, 228, 0.3)',
    backdropFilter: 'blur(3px)',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  loadingText: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  footer: {
    zIndex: 1200, // same as sidebar
    position: 'fixed',
    bottom: 0,
    width: 'calc(100% - 264px)',
    left: 264,
    backgroundColor: 'white',
    boxShadow: '0px -4px 12px rgba(222, 222, 222, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    background: 'white',
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
      left: 0,
      width: '100%',
    },
  },
  footerText: {
    fontWeight: 600,
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  saveButton: {
    paddingInline: '2rem',
  },
}));

export default function BlogForm() {
  const classes = useStyles();

  const {createPost, updatePost, loading, error} = useBlogPostActions();
  const {showToast} = useToastActions();

  const {blogId} = useParams();
  const history = useHistory();

  const {post} = useBlogPost(blogId || '');

  const methods = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {draft, clearDraft} = useBlogFormDraftValue();

  const {register, setValue} = methods;

  useEffect(() => {
    register(BLOG_FORM_FIELDS.enabled);
    register(BLOG_FORM_FIELDS.urlIsCustom);
    register(BLOG_FORM_FIELDS.url);
    register(BLOG_FORM_FIELDS.poster, {
      required: 'Este campo es requerido',
    });
    register(BLOG_FORM_FIELDS.posterAlt);
    register(BLOG_FORM_FIELDS.author, {
      required: 'Este campo es requerido',
    });
    register(BLOG_FORM_FIELDS.enabledRelatedEntities);
    register(BLOG_FORM_FIELDS.postFeatured);
  }, [register]);

  useEffect(() => {
    if (!post) return;
    setValue(BLOG_FORM_FIELDS.title, post.title);
    setValue(BLOG_FORM_FIELDS.author, post.author.name);

    setValue(BLOG_FORM_FIELDS.categories, post.categories);
    setValue(BLOG_FORM_FIELDS.urlIsCustom, post.isCustomSlug);

    setValue(BLOG_FORM_FIELDS.url, post.slug);

    setValue(BLOG_FORM_FIELDS.seoTitle, post.seoTitle);
    setValue(BLOG_FORM_FIELDS.seoDescription, post.seoContent);
    setValue(BLOG_FORM_FIELDS.poster, post.mainImageId);
    setValue(BLOG_FORM_FIELDS.posterAlt, post.mainImageDescription);

    setValue(BLOG_FORM_FIELDS.enabled, post.enabled);
    setValue(BLOG_FORM_FIELDS.postFeatured, post.featured);
    setValue(
      BLOG_FORM_FIELDS.enabledRelatedEntities,
      post.enabledRelatedEntities
    );
  }, [post, setValue]);

  useEffect(() => {
    if (post) return;

    if (!draft) return;

    setValue(BLOG_FORM_FIELDS.title, draft.title);
    console.log({draft});
    setValue(BLOG_FORM_FIELDS.author, draft.author);

    setValue(BLOG_FORM_FIELDS.categories, draft.categories);
    setValue(BLOG_FORM_FIELDS.urlIsCustom, draft.urlIsCustom);

    setValue(BLOG_FORM_FIELDS.url, draft.url);

    setValue(BLOG_FORM_FIELDS.seoTitle, draft.seoTitle);
    setValue(BLOG_FORM_FIELDS.seoDescription, draft.seoDescription);
    setValue(BLOG_FORM_FIELDS.posterAlt, draft.posterAlt);

    setValue(BLOG_FORM_FIELDS.enabled, draft.enabled);
    setValue(BLOG_FORM_FIELDS.postFeatured, draft.postFeatured);
    setValue(
      BLOG_FORM_FIELDS.enabledRelatedEntities,
      draft.enabledRelatedEntities
    );
  }, [draft, post, setValue]);

  const handleSubmit = useCallback(
    async (data: BlogFormFields) => {
      setIsSubmitting(true);
      setSubmitError('');

      try {
        let success = false;

        if (post) {
          success = await updatePost(post.id, data);

          if (success) {
            showToast({
              type: 'success',
              title: 'Entrada actualizada',
              message: 'La entrada del blog se ha actualizado correctamente',
              duration: 4000,
            });
            clearDraft();
            history.push('/my-store/blog');
          } else {
            throw new Error('No se pudo actualizar la entrada del blog');
          }
        } else {
          success = await createPost(data);

          if (success) {
            showToast({
              type: 'success',
              title: 'Entrada creada',
              message: 'La nueva entrada del blog se ha creado correctamente',
              duration: 4000,
            });
            clearDraft();
            history.push('/my-store/blog');
          } else {
            throw new Error('No se pudo crear la entrada del blog');
          }
        }
      } catch (err) {
        const errorMessage = err?.message || 'Ocurrió un error inesperado';
        setSubmitError(errorMessage);

        showToast({
          type: 'danger',
          title: 'Error',
          message: errorMessage,
          duration: 6000,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [post, updatePost, createPost, showToast, clearDraft, history]
  );

  const isFormLoading = loading || isSubmitting;

  return (
    <div className={classes.container}>
      {isFormLoading && (
        <Backdrop open={true} className={classes.loadingBackdrop}>
          <Fade in={true}>
            <div className={classes.loadingContent}>
              <CircularProgress color="primary" size={48} />
              <Typography className={classes.loadingText}>
                {post ? 'Actualizando entrada...' : 'Creando entrada...'}
              </Typography>
            </div>
          </Fade>
        </Backdrop>
      )}

      <FormProvider {...methods}>
        <form
          className={classes.form}
          onSubmit={methods.handleSubmit(handleSubmit)}
        >
          <InformationSection />
          <ContentSection />
          <SeoSection />
          <PostFeatured />
          <RelatedProducts />

          <footer className={classes.footer}>
            <Typography className={classes.footerText}>
              {post ? 'Editando entrada' : 'Entrada nueva'}
            </Typography>

            <div className={classes.footerActions}>
              <FormControlLabel
                control={
                  <IosSwitch
                    checked={!!methods.watch(BLOG_FORM_FIELDS.enabled)}
                    onChange={e =>
                      setValue(BLOG_FORM_FIELDS.enabled, e.target.checked)
                    }
                    disabled={isFormLoading}
                  />
                }
                label="Publicar"
                labelPlacement="start"
              />

              <CustomButton
                variant="contained"
                className={classes.saveButton}
                type="submit"
                disabled={isFormLoading}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {isSubmitting
                  ? post
                    ? 'Actualizando...'
                    : 'Creando...'
                  : 'Guardar'}
              </CustomButton>
            </div>
          </footer>
        </form>
      </FormProvider>

      {/* Enhanced error display */}
      <Toast open={!!(error || submitError)} closeDelay={5000} severity="error">
        {error || submitError}
      </Toast>
    </div>
  );
}
